import React from "react";
import PropTypes from "prop-types";
import {
  Card,
  CardContent,
  Typography,
  Box,
} from "@mui/material";
import { colors, customShadows, borderRadius, borderRadiusPx } from "../../../../theme";
import { CommonButton, CommonTable, CommonChip } from "../../ui";

/**
 * TopPerformingTableCard - Data table for top-performing lotteries, games, and campaigns.
 */
const TopPerformingTableCard = ({
  title = "",
  columns,
  rows = [],
  viewAllText = "",
  onViewAllClick,
  currencyPrefix = "",
  currencySuffix = "",
  sx = {},
}) => {
  const defaultColumns = [
    {
      id: "game",
      label: "Game / Lottery",
      align: "left",
      render: (val, row) => (
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.84rem", color: colors.slate[900] }}>
          {row.game || row.name || row.title || val}
        </Typography>
      ),
    },
    {
      id: "creator",
      label: "Creator / Partner",
      align: "left",
      render: (val, row) => (
        <Typography
          variant="body2"
          sx={{
            fontWeight: 500,
            fontSize: "0.82rem",
            color: row.creatorColor || (row.isSpecial ? colors.error.main : colors.slate[600]),
          }}
        >
          {row.creator || row.partner || val || "AutoBET"}
        </Typography>
      ),
    },
    {
      id: "revenue",
      label: "Game Revenue",
      align: "right",
      render: (val, row) => (
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.84rem", color: colors.slate[900] }}>
          {currencyPrefix}{row.revenue || row.totalPool || val || "0.00"}{currencySuffix}
        </Typography>
      ),
    },
    {
      id: "payouts",
      label: "Player Payouts",
      align: "right",
      render: (val, row) => (
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.82rem", color: colors.slate[700] }}>
          {currencyPrefix}{row.payouts || row.claimed || val || "0.00"}{currencySuffix}
        </Typography>
      ),
    },
    {
      id: "tax",
      label: "Tax",
      align: "right",
      render: (val, row) => (
        <Typography variant="body2" sx={{ fontWeight: 500, fontSize: "0.82rem", color: colors.slate[500] }}>
          {currencyPrefix}{row.tax || row.platformFee || val || "0.00"}{currencySuffix}
        </Typography>
      ),
    },
    {
      id: "netRevenue",
      label: "Net Creator Revenue",
      align: "right",
      render: (val, row) => (
        <Typography variant="body2" sx={{ fontWeight: 600, fontSize: "0.84rem", color: colors.slate[900] }}>
          {currencyPrefix}{row.netRevenue || row.creatorShare || val || "0.00"}{currencySuffix}
        </Typography>
      ),
    },
    {
      id: "status",
      label: "Status",
      align: "center",
      render: (val, row) => (
        <CommonChip status={row.status || val || "Active"} />
      ),
    },
  ];

  const tableColumns = columns || defaultColumns;

  return (
    <Card
      sx={{
        borderRadius: borderRadiusPx.card,
        border: `1px solid ${colors.dashboard.cardBorder}`,
        boxShadow: customShadows.cardSubtle,
        backgroundColor: colors.dashboard.cardBg,
        overflow: "hidden",
        ...sx,
      }}
    >
      <CardContent sx={{ p: { xs: 2, sm: 3 } }}>
        {title && (
          <Typography
            variant="h6"
            sx={{
              fontWeight: 700,
              fontSize: "1.05rem",
              color: colors.dashboard.title,
              mb: 2,
              letterSpacing: "-0.01em",
            }}
          >
            {title}
          </Typography>
        )}

        {rows && rows.length > 0 && (
          <CommonTable
            columns={tableColumns}
            rows={rows}
            minWidth={680}
          />
        )}

        {viewAllText && (
          <Box sx={{ display: "flex", justifyContent: "center", mt: 3, mb: 0.5 }}>
            <CommonButton
              variant="outlined"
              onClick={onViewAllClick}
            >
              {viewAllText}
            </CommonButton>
          </Box>
        )}
      </CardContent>
    </Card>
  );
};

TopPerformingTableCard.propTypes = {
  title: PropTypes.string,
  columns: PropTypes.arrayOf(
    PropTypes.shape({
      id: PropTypes.string.isRequired,
      label: PropTypes.node.isRequired,
      align: PropTypes.string,
      render: PropTypes.func,
    })
  ),
  rows: PropTypes.arrayOf(PropTypes.object),
  viewAllText: PropTypes.string,
  onViewAllClick: PropTypes.func,
  currencyPrefix: PropTypes.string,
  currencySuffix: PropTypes.string,
  sx: PropTypes.object,
};

export default TopPerformingTableCard;
